/* Resolves the expense line-coding dropdown sources from the per-entity COA custom
   attributes (V011): the `branch`, `product_group`, and `supplier` LIST attributes seeded
   from Agresso (V017). Looks up each attribute by its stable key, then loads its allowed
   values. Returned lists feed the AddLineForm selects; the chosen values are written to the
   line's attribute_values JSONB. */
import {
  useAttributeValues,
  useCoaAttributes,
  type CoaAttributeValue,
} from '@/expense/hooks/use-coa';

export interface ExpenseCoding {
  branch: CoaAttributeValue[];
  productGroup: CoaAttributeValue[];
  supplier: CoaAttributeValue[];
  loading: boolean;
}

/** Attribute keys that back the coding dropdowns (must match the V017 seed keys). */
export const CODING_KEYS = {
  branch: 'branch',
  productGroup: 'product_group',
  supplier: 'supplier',
} as const;

export function useExpenseCoding(): ExpenseCoding {
  const { data: attributes = [], isLoading: attrsLoading } = useCoaAttributes();
  const idFor = (key: string) =>
    attributes.find((a) => a.key === key && a.active)?.attributeId ?? null;

  const branchQ = useAttributeValues(idFor(CODING_KEYS.branch));
  const productGroupQ = useAttributeValues(idFor(CODING_KEYS.productGroup));
  const supplierQ = useAttributeValues(idFor(CODING_KEYS.supplier));

  const active = (v: CoaAttributeValue) => v.active;
  return {
    branch: (branchQ.data ?? []).filter(active),
    productGroup: (productGroupQ.data ?? []).filter(active),
    supplier: (supplierQ.data ?? []).filter(active),
    loading:
      attrsLoading ||
      branchQ.isLoading ||
      productGroupQ.isLoading ||
      supplierQ.isLoading,
  };
}
